import { useEffect, useRef } from 'react'
import { useLocation, useHistory } from 'react-router'
import { pipe, prop, reject, join, isEmpty, values, head, path } from 'ramda'
import { useDialogControllers } from '@machobear/design-system'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { usePersonaIdVerification } from '@/features/Persona'
import { Box, Stack } from '@/components/structure'
import { AvatarPicker, defaultAvatar } from '@/components/avatar'
import BrokerLogo from '@/components/broker-logo'
import ContentWrapper from '@/components/content-wrapper'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import VerifiedBadge from '@/components/verified-badge'
import useProfile from '@/hooks/use-profile'
import { Button } from '@/components/button'
import { colors, breakpoints } from '@/styles'
import { isFalsy, isNullOrEmpty } from '@/utils'
import { formatPhone } from '@/utils/format-phone'
import { getImageUrl } from '@/utils/image'
import { ProfileIcon } from '@/components/icons'
import { Label, H4, ParagraphMedium } from '@/components/text'
import Skeleton from '@/components/skeleton'
import useMediaQuery from '@/hooks/use-media-query'
import { showEditLandlordProfileModal } from '@/features/Profile/EditLandlordProfileModal'
import { showVerifyBusinessLicenceModal } from '@/features/Profile/VerifyBusinessLicenceModal'
import StatusBadge from '@/components/status-badge'
import { MenuWrapper, MenuItem, Title, Description, MenuButton } from './menu-item'
import Edit from './illustration/edit.svg?react'
import IdVerificationRequiredModal from './id-verification-required-modal'

const AvatarWrapper = styled.div`
  display: flex;
  justify-content: center;
`

const NameText = styled(H4)`
  word-break: break-word;
`

const ProfileDetails = styled.div`
  display: grid;
  width: 100%;
  flex-wrap: wrap;
  grid-template-columns: repeat(2, minmax(min(0px, 100%), 1fr));
  gap: 16px 0;
`

const DetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px 0;
  ${props =>
    props.width !== '50%' &&
    `
    grid-column: 1 / 2 span;
  `};
`

const DetailText = styled(ParagraphMedium)`
  color: ${colors.grey56};
`

const BrokerText = styled(DetailText)`
  word-break: break-all;
`

const NewPanel = styled.div`
  margin-top: 16px;
  background: ${colors.white};
  padding: 24px;
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  border: 1px solid ${colors.grey88};
  overflow-x: hidden;
  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 20px;
  }
`

const AdminButtonWrapper = styled.div`
  display: flex;
  gap: 0 8px;
  margin-top: 16px;
  > a {
    white-space: pre;
  }
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: space-between;
    > a {
      width: 100%;
    }
  }
`

const NameWrapper = styled.div`
  display: flex;
  gap: 0 16px;
  align-items: flex-start;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    justify-content: space-between;
  }
`

const Divider = styled.div`
  height: 1px;
  margin: 16px 0;
  background-color: ${colors.grey88};
`

const getImage = (url, options) => (url ? getImageUrl(url, options) : defaultAvatar)

const formatAddress = (companyInfo = {}) => {
  const { unit_number, address, city, state, zip } = companyInfo
  if (!address && !city && !state && !zip) return '-'
  const streetAddress = address && (unit_number ? `${unit_number} - ${address}` : address)
  const formattedAddress = pipe(
    reject(isFalsy),
    join(', ')
  )([streetAddress, city, state, zip?.toUpperCase()])
  return formattedAddress
}

const LandlordProfile = () => {
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const profile = useProfile()
  const isLoadingProfile = isNullOrEmpty(profile.id)
  const personaIdVerification = usePersonaIdVerification()
  const brokerInfo = {
    name: path(['company_info', 'broker', 'name'], profile),
    logo: pipe(values, head, prop(['location']))(path(['files', 'broker_logo'], profile))
  }
  const hasBroker = !!brokerInfo?.name
  const isCompany = profile.account_type === 'business'

  const identityVerified = profile.identity_verified_state === 'verified'
  const identityVerificationPending = profile.identity_verified_state === 'pending'

  const companyVerifiedState = profile.company_info?.business_licence_verified_state
  const isCompanyVerified = companyVerifiedState === 'verified'
  const isCompanyVerificationPending =
    companyVerifiedState === 'pending' || companyVerifiedState === 'verifying'
  const isCompanyVerificationFailed = companyVerifiedState === 'verification_failed'
  const isTenant = profile.user_role === 'tenant'
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const location = useLocation()
  const history = useHistory()
  const hasModalShownRef = useRef(false)
  const hasBusinessVerificationModalShownRef = useRef(false)

  const { visible, showModal, closeModal } = useDialogControllers({
    requiredModal: false,
    successModal: false
  })

  useEffect(() => {
    if (location?.state?.showEditLandlordProfileModal && !hasModalShownRef.current) {
      showEditLandlordProfileModal()
      hasModalShownRef.current = true
    }
  }, [location])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const openVerifyUserModal = searchParams.get('openVerifyUserModal')
    if (openVerifyUserModal) {
      showModal('requiredModal')()
    }
    searchParams.delete('openVerifyUserModal')
    const newSearch = searchParams.toString()
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '')

    history.replace(newUrl)
  }, [history, location.pathname, location.search])

  const getCompanyStatusBadge = () => {
    if (isCompanyVerified)
      return <StatusBadge type="success" title={t('common.verified', 'Verified')} />
    if (isCompanyVerificationPending)
      return <StatusBadge type="default" title={t('l.profile.view.verifying.text', 'Verifying')} />
    if (isCompanyVerificationFailed)
      return (
        <StatusBadge
          type="error"
          title={t('l.profile.view.verification_failed.text', 'Verification Failed')}
        />
      )
    return null
  }

  const handleShowEditLandlordProfileModal = () => {
    showEditLandlordProfileModal()
  }

  const handleShowVerifyBusinessLicenceModal = () => {
    showVerifyBusinessLicenceModal()
  }
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const openVerifyBusinessLicence = searchParams.get('openVerifyBusinessLicenceModal')
    if (openVerifyBusinessLicence && !hasBusinessVerificationModalShownRef.current) {
      handleShowVerifyBusinessLicenceModal()
      hasBusinessVerificationModalShownRef.current = true
    }
    searchParams.delete('openVerifyBusinessLicenceModal')
    const newSearch = searchParams.toString()
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '')
    history.replace(newUrl)
  }, [history, location.pathname, location.search])

  if (isEmpty(profile)) return null

  const showAdminDashboard = () => {
    return (
      (isCompany || profile.account_type === 'family') &&
      profile.user_role === 'administrator' && (
        <AdminButtonWrapper>
          <Button theme="secondary" size="small" to="/manage/users/">
            {t('l.profile.manage_users.label', 'Manage Users')}
          </Button>
          <Button theme="secondary" size="small" to="/manage/listings/">
            {t('l.profile.manage_listings.label', 'Manage Listings')}
          </Button>
        </AdminButtonWrapper>
      )
    )
  }

  const handleUploadAvatar = file => {
    dispatch.profile.uploadAvatar({
      file,
      fileId: profile.avatar?.id
    })
  }

  const Verified = props => (
    <VerifiedBadge
      type="id"
      width={26}
      height={26}
      bottom={-5}
      verified={profile.identity_verified_state}
      {...props}
    />
  )

  const Address = () => (
    <DetailWrapper width={displayMobile || !isCompany ? '100%' : '50%'}>
      <Label>{t('l.profile.view.address.text', 'Address')}</Label>
      <DetailText>{formatAddress(companyInfo)}</DetailText>
    </DetailWrapper>
  )

  const BrokerInfo = () => {
    if (!hasBroker) return null
    return (
      <DetailWrapper width={displayMobile ? '100%' : '50%'}>
        <Label>{t('l.profile.view.broker_name.text', 'Brokerage')}</Label>
        <BrokerText>{brokerInfo.name || '-'}</BrokerText>
        <BrokerLogo broker={brokerInfo} />
      </DetailWrapper>
    )
  }

  const AboutMe = () => (
    <DetailWrapper>
      <Label>{t('l.profile.view.about.text', 'About')}</Label>
      <DetailText>{profile.about_me || '-'}</DetailText>
    </DetailWrapper>
  )

  const showBusinessDetails = () => (
    <ProfileDetails>
      <AboutMe />
      <DetailWrapper width={displayMobile ? '100%' : '50%'}>
        <Label>{t('l.profile.view.units_managed.text', 'Units Managed')}</Label>
        <DetailText>{profile.work_history_properties_managed || '-'}</DetailText>
      </DetailWrapper>
      <DetailWrapper width={displayMobile ? '100%' : '50%'}>
        <Label>{t('l.profile.view.work_history.text', 'Work History')}</Label>
        <DetailText>
          {profile.work_history_year_started
            ? `${t('l.profile.view.since.text', 'Since')}${' '}
        ${profile.work_history_year_started}`
            : '-'}
        </DetailText>
      </DetailWrapper>
    </ProfileDetails>
  )

  const showPersonalDetails = () => (
    <ProfileDetails>
      <AboutMe />
      <Address />
    </ProfileDetails>
  )

  const showProfileDetails = () => {
    if (isLoadingProfile)
      return (
        <Stack gutter={12} width={displayMobile ? '100%' : 'unset'} flex="1">
          <Skeleton height={28} width={200} />
          <Skeleton height={16} width={130} />
          <Skeleton height={16} width={180} />
        </Stack>
      )

    const uniquePreferredName = profile.first_name !== profile.preferred_name
    return (
      <Stack gutter={8} width={displayMobile ? '100%' : 'unset'} flex="1">
        <NameText>
          {`${profile.first_name} ${profile.last_name}`}
          {uniquePreferredName && ` (${profile.preferred_name})`} <Verified />
        </NameText>
        <Box color={colors.grey56}>
          <Stack gutter={4}>
            <ParagraphMedium>
              {formatPhone(profile.phone_number, profile.phone_country_code_alpha)}
            </ParagraphMedium>
            <ParagraphMedium>{profile.username}</ParagraphMedium>
          </Stack>
        </Box>
      </Stack>
    )
  }

  const showCompanyDetails = () => (
    <>
      <Box top={32} bottom={16}>
        <Title>{t('l.profile.view.company_info.title', 'Company Info')}</Title>
      </Box>
      <ProfileDetails>
        <DetailWrapper width={displayMobile ? '100%' : '50%'}>
          <Label>{t('l.profile.view.company_name.text', 'Name')}</Label>
          <DetailText>{profile.company_info?.name || '-'}</DetailText>
        </DetailWrapper>
        <DetailWrapper width={displayMobile ? '100%' : '50%'}>
          <Label>{t('l.profile.view.phone.text', 'Phone')}</Label>
          <DetailText>
            {formatPhone(
              profile.company_info?.phone_number,
              profile.company_info?.phone_country_code_alpha
            ) || '-'}
          </DetailText>
        </DetailWrapper>
        <DetailWrapper width={displayMobile ? '100%' : '50%'}>
          <Label>{t('l.profile.view.email.text', 'Email')}</Label>
          <DetailText>{profile.company_info?.email || '-'}</DetailText>
        </DetailWrapper>
        <Address />
        <BrokerInfo />
      </ProfileDetails>
    </>
  )

  const companyInfo = profile.company_info
  const avatarLocation = profile.avatar?.aws_s3_key

  return (
    <Page>
      <IdVerificationRequiredModal
        visible={visible.requiredModal}
        onClose={closeModal('requiredModal')}
      />
      <ContentWrapper className="mb-50" padding="16px">
        <Helmet>
          <title>{t('l.profile.view.my_profile.title', 'Profile')}</title>
        </Helmet>
        <PageTitle>
          <ProfileIcon />
          {t('l.profile.view.my_profile.title', 'Profile')}
        </PageTitle>
        <NewPanel>
          <Stack gutter={16}>
            <NameWrapper>
              <AvatarWrapper>
                <AvatarPicker
                  location={getImage(avatarLocation, { width: 240, height: 240 })}
                  width={64}
                  height={64}
                  right={0}
                  onSelect={handleUploadAvatar}
                  showIcon={!avatarLocation}
                />
              </AvatarWrapper>
              {!displayMobile && showProfileDetails()}
              <Button
                size="small"
                theme="secondary"
                onClick={handleShowEditLandlordProfileModal}
                shape={displayMobile ? 'round' : 'default'}
                aria-label={
                  displayMobile ? t('l.profile.edit.edit_profile.title', 'Edit Profile') : undefined
                }
                title={
                  displayMobile ? t('l.profile.edit.edit_profile.title', 'Edit Profile') : undefined
                }
              >
                {!displayMobile ? t('l.profile.edit.edit_profile.title', 'Edit Profile') : <Edit />}
              </Button>
            </NameWrapper>
            {displayMobile && showProfileDetails()}
            {showAdminDashboard()}
          </Stack>
          <Divider aria-hidden />
          {isCompany ? showBusinessDetails() : showPersonalDetails()}
          {isCompany && showCompanyDetails()}
        </NewPanel>

        <MenuWrapper>
          {isLoadingProfile ? (
            <>
              <Skeleton height={98} width="100%" borderRadius={8} />
              <Skeleton height={98} width="100%" borderRadius={8} />
              <Skeleton height={98} width="100%" borderRadius={8} />
            </>
          ) : (
            <>
              {!identityVerified && (
                <MenuItem
                  onClick={() => personaIdVerification({})}
                  disabled={identityVerificationPending}
                  button={
                    !identityVerificationPending && (
                      <MenuButton theme="primary">
                        {t('b.settings.view.verify_now.text', 'Verify Now')}
                      </MenuButton>
                    )
                  }
                  badge={
                    identityVerificationPending && (
                      <StatusBadge
                        type="default"
                        title={t('l.profile.view.verifying.text', 'Verifying')}
                      />
                    )
                  }
                >
                  <Title>
                    {t('b.settings.view.become_trusted.label', 'Become Trusted')}
                    <VerifiedBadge
                      type={identityVerified ? 'id' : 'grey'}
                      width={30}
                      height={22}
                      verified
                    />
                  </Title>
                  <Description>
                    {t(
                      'b.settings.view.become_trusted_landlord.description',
                      'Verify ID to improve your credibility.'
                    )}
                  </Description>
                </MenuItem>
              )}
              {isCompany && (
                <MenuItem
                  onClick={handleShowVerifyBusinessLicenceModal}
                  disabled={isCompanyVerified || isCompanyVerificationPending}
                  button={
                    !isCompanyVerified &&
                    !isCompanyVerificationPending && (
                      <MenuButton theme="primary">
                        {t('b.settings.view.verify_now.text', 'Verify Now')}
                      </MenuButton>
                    )
                  }
                  badge={getCompanyStatusBadge()}
                >
                  <Title>
                    {t('b.settings.view.verify_business.label', 'Verify Your Business License')}
                    <VerifiedBadge
                      type={isCompanyVerified ? 'id' : 'grey'}
                      width={30}
                      height={22}
                      verified
                    />
                  </Title>
                  <Description>
                    {t(
                      'l.profile.view.verify_business.description',
                      'Become Verified by providing your Business License Number or your Brokerage License.'
                    )}
                  </Description>
                </MenuItem>
              )}
              <MenuItem
                to="/settings/preferences"
                button={
                  <MenuButton theme="secondary">
                    {displayTablet
                      ? t('b.settings.view.edit_preferences.label', 'Edit Preferences')
                      : t('common.edit', 'Edit')}
                  </MenuButton>
                }
              >
                <Title>{t('b.settings.view.preferences.label', 'Preferences')}</Title>
                <Description>
                  {t(
                    'b.settings.view.preferences.description',
                    'Measurement units, language settings...'
                  )}
                </Description>
              </MenuItem>
              <MenuItem
                to={
                  isMobile
                    ? '/settings'
                    : isTenant
                      ? '/settings/payment-settings'
                      : '/settings/plan-settings'
                }
                button={
                  <MenuButton theme="secondary">
                    {displayTablet
                      ? t('b.settings.view.edit_account.button', 'Edit Account')
                      : t('common.edit', 'Edit')}
                  </MenuButton>
                }
              >
                <Title>{t('b.settings.view.account_settings.label', 'Account Settings')}</Title>
                <Description>
                  {t(
                    'b.settings.view.account_settings.description',
                    'Payment settings, change password...'
                  )}
                </Description>
              </MenuItem>
            </>
          )}
        </MenuWrapper>
      </ContentWrapper>
    </Page>
  )
}

export default LandlordProfile
